import { Briefcase, Clock, Database, FileText, Server, Users, type LucideIcon } from 'lucide-react'
import type { ReportDomain, ReportMetadata } from '../../types'

export const ICON_MAP: Record<string, LucideIcon> = {
  Clock,
  Users,
  Briefcase,
  Database,
  Server,
  FileText,
}

export const DOMAIN_ICON_MAP: Record<ReportDomain, LucideIcon> = {
  licensing: Clock,
  users: Users,
  cases: Briefcase,
  'data-usage': Database,
  infrastructure: Server,
}

export interface DomainColours {
  bg: string
  text: string
  border: string
}

export const DOMAIN_COLOUR_MAP: Record<ReportDomain, DomainColours> = {
  licensing: {
    bg: 'bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10',
    text: 'text-[#5c6e94] dark:text-[#8a9bba]',
    border: 'border-[#4e5e82]/15 dark:border-[#4e5e82]',
  },
  users: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
  },
  cases: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
  },
  'data-usage': {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800',
  },
  infrastructure: {
    bg: 'bg-stone-50 dark:bg-stone-800/50',
    text: 'text-stone-700 dark:text-stone-300',
    border: 'border-stone-200 dark:border-stone-700',
  },
}

export const DOMAIN_ORDER: ReportDomain[] = [
  'licensing',
  'users',
  'cases',
  'data-usage',
  'infrastructure',
]

interface FilterArgs {
  reports: ReportMetadata[]
  searchQuery: string
  selectedDomain: ReportDomain | 'all'
}

export function filterReports({
  reports,
  searchQuery,
  selectedDomain,
}: FilterArgs): ReportMetadata[] {
  const lower = searchQuery.toLowerCase()
  return reports.filter((report) => {
    const matchesSearch =
      searchQuery === '' ||
      report.name.toLowerCase().includes(lower) ||
      report.description.toLowerCase().includes(lower)
    const matchesDomain = selectedDomain === 'all' || report.domain === selectedDomain
    return matchesSearch && matchesDomain
  })
}

export function groupByDomain(
  reports: ReportMetadata[]
): Partial<Record<ReportDomain, ReportMetadata[]>> {
  return reports.reduce<Partial<Record<ReportDomain, ReportMetadata[]>>>((acc, report) => {
    const existing = acc[report.domain] ?? []
    acc[report.domain] = [...existing, report]
    return acc
  }, {})
}
